import { forwardRef, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";

/**
 * Textarea — Aurora-themed multiline input (Faza 8 polish).
 *
 * Features:
 *   - autoResize prop — content height bo'yicha avtomatik o'sadi (min/max bilan)
 *   - showCount — character counter (max prop bilan)
 *   - invalid — error border + ring
 *   - tokenlardan: --form-field-* (bir Input bilan bir xil)
 *
 * A11y:
 *   - aria-invalid avtomatik
 *   - aria-describedby — character counter ID
 */
export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "rows"> {
  invalid?: boolean;
  /** Auto-resize: content height bo'yicha o'sadi */
  autoResize?: boolean;
  /** Min row (auto resize floor) */
  minRows?: number;
  /** Max row (auto resize ceil) */
  maxRows?: number;
  /** Character counter ko'rsatish (max bilan birga ishlatilsin) */
  showCount?: boolean;
  /** Counter o'rniga matn rang ogohlantirishi (0.85 max'dan boshlab amber) */
  warnAtRatio?: number;
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") ref(node);
      else if (ref && typeof ref === "object")
        (ref as React.MutableRefObject<T | null>).current = node;
    });
  };
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      invalid,
      autoResize = false,
      minRows = 3,
      maxRows = 10,
      showCount = false,
      warnAtRatio = 0.85,
      maxLength,
      onChange,
      value,
      defaultValue,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      id,
      ...props
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const [length, setLength] = useState(() =>
      typeof value === "string"
        ? value.length
        : typeof defaultValue === "string"
          ? defaultValue.length
          : 0,
    );

    useEffect(() => {
      if (typeof value === "string") setLength(value.length);
    }, [value]);

    // Auto-resize logic
    const adjustHeight = () => {
      const el = innerRef.current;
      if (!el || !autoResize) return;
      el.style.height = "auto";
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 22;
      const minHeight = lineHeight * minRows + 16; // padding
      const maxHeight = lineHeight * maxRows + 16;
      const next = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
      el.style.height = `${next}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
    };

    useEffect(() => {
      adjustHeight();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, autoResize, minRows, maxRows]);

    const counterId = id ? `${id}-count` : undefined;
    const describedBy = [ariaDescribedBy, showCount ? counterId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

    const ratio = maxLength ? length / maxLength : 0;
    const overLimit = maxLength ? length > maxLength : false;
    const warnLimit = maxLength ? ratio >= warnAtRatio : false;

    return (
      <div className="w-full">
        <textarea
          id={id}
          ref={mergeRefs(innerRef, ref)}
          aria-invalid={ariaInvalid ?? (invalid ? true : undefined)}
          aria-describedby={describedBy}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          onChange={(e) => {
            setLength(e.target.value.length);
            adjustHeight();
            onChange?.(e);
          }}
          rows={autoResize ? minRows : minRows}
          className={cn(
            "w-full rounded-lg border px-3 py-2.5 text-sm",
            "bg-[var(--form-field-bg)] border-[var(--form-field-border)] text-[var(--text-primary)]",
            "placeholder:text-[var(--form-field-placeholder)]",
            "transition-colors duration-150 resize-y",
            "hover:border-[var(--form-field-border-hover)]",
            "focus:border-[var(--form-field-border-focus)] focus:bg-[var(--form-field-bg-focus)]",
            "focus:outline-none focus:ring-2 focus:ring-[var(--form-field-ring-focus)]",
            "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[var(--form-field-bg-disabled)]",
            "read-only:bg-[var(--form-field-bg-disabled)] read-only:text-[var(--text-secondary)]",
            invalid &&
              "border-[var(--form-error-border)] focus:border-[var(--form-error-border-focus)] focus:ring-[var(--form-error-ring)]",
            autoResize && "resize-none",
            className,
          )}
          {...props}
        />
        {showCount && (
          <div
            id={counterId}
            className={cn(
              "mt-1 flex justify-end text-[11px] tabular-nums",
              overLimit
                ? "text-[var(--form-counter-over-fg)]"
                : warnLimit
                  ? "text-[var(--form-counter-warn-fg)]"
                  : "text-[var(--form-counter-fg)]",
            )}
            aria-live="polite"
          >
            {length}
            {maxLength != null && (
              <>
                <span className="opacity-50 mx-0.5">/</span>
                <span>{maxLength}</span>
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);
Textarea.displayName = "Textarea";
